import { alpha } from '@mui/material/styles';
import moment from 'moment';
import { CALENDAR_GRID_DAY_COUNT } from './dateUtils';

export const CALENDAR_LOAD_FADE_MS = 380;
export const CALENDAR_VIEW_ENTER_MS = 280;

export function calendarViewEnterSx(isActive) {
  return {
    width: '100%',
    transition: `opacity ${CALENDAR_VIEW_ENTER_MS}ms cubic-bezier(0.4, 0, 0.2, 1), transform ${CALENDAR_VIEW_ENTER_MS}ms cubic-bezier(0.4, 0, 0.2, 1)`,
    opacity: isActive ? 1 : 0,
    transform: isActive ? 'translateY(0)' : 'translateY(6px)',
    pointerEvents: isActive ? 'auto' : 'none',
    position: isActive ? 'relative' : 'absolute',
    inset: isActive ? undefined : 0,
  };
}

export function calendarViewStackSx() {
  return {
    position: 'relative',
    width: '100%',
    minHeight: 0,
  };
}

export function calendarDataBoneSx(isDark, extra = {}) {
  return {
    bgcolor: isDark ? alpha('#ffffff', 0.1) : alpha('#94a3b8', 0.16),
    transform: 'scale(1)',
    transformOrigin: 'left center',
    animation: 'calendarBonePulse 1.6s ease-in-out infinite',
    '@keyframes calendarBonePulse': {
      '0%, 100%': { opacity: isDark ? 0.45 : 0.38 },
      '50%': { opacity: isDark ? 0.9 : 0.62 },
    },
    ...extra,
  };
}

export function calendarLoadingOverlaySx(isDark) {
  return {
    position: 'absolute',
    inset: 0,
    pointerEvents: 'none',
    zIndex: 3,
    background: isDark
      ? 'linear-gradient(180deg, rgba(18,21,28,0.08) 0%, rgba(18,21,28,0.18) 100%)'
      : 'linear-gradient(180deg, rgba(255,255,255,0.04) 0%, rgba(255,255,255,0.14) 100%)',
    transition: 'opacity 0.35s ease',
  };
}

export function calendarContentRevealSx(isLoading) {
  return {
    transition: 'opacity 0.32s cubic-bezier(0.4, 0, 0.2, 1), filter 0.32s cubic-bezier(0.4, 0, 0.2, 1)',
    opacity: isLoading ? 0.92 : 1,
    filter: isLoading ? 'saturate(0.94) blur(0.2px)' : 'none',
  };
}

/** Shell payload so grid/ultra render real layout while counts and cells load. */
export function buildWorkspacePlaceholderCalendarData({
  roomCount = 6,
  anchorDate,
  gridDayCount = CALENDAR_GRID_DAY_COUNT,
  displayView = 'ultra',
}) {
  const anchor = moment(anchorDate || new Date()).startOf('day');
  let startMoment = anchor.clone();
  let endMoment = anchor.clone();

  if (displayView === 'ultra') {
    endMoment = startMoment.clone();
  } else if (displayView === 'month') {
    const monthStart = anchor.clone().startOf('month');
    const monthEnd = anchor.clone().endOf('month');
    startMoment = monthStart.clone().startOf('week');
    endMoment = monthEnd.clone().endOf('week');
  } else {
    endMoment = startMoment.clone().add(Math.max(1, gridDayCount) - 1, 'days');
  }

  const rooms = Array.from({ length: Math.max(1, roomCount) }, (_, index) => ({
    id: `__placeholder_${index}`,
    room_number: '',
    room_code: '',
    room_type_id: '__placeholder_type',
    room_type_name: '',
    __placeholder: true,
  }));

  return {
    rooms,
    room_types: [{ id: '__placeholder_type', name: 'All Rooms' }],
    bookings: [],
    booking_items_by_date: {},
    blocked_rooms_by_date: {},
    start_date: startMoment.format('YYYY-MM-DD'),
    end_date: endMoment.format('YYYY-MM-DD'),
    __placeholder: true,
  };
}

export function isPlaceholderCalendarData(data) {
  return Boolean(data?.__placeholder);
}
